import React from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import { VolumeUp as VolumeUpIcon } from "@mui/icons-material";
import RichTextDisplay from "../RichTextDisplay";
import QuestionImage from "@/pages/user/ExamSession/QuestionImage";
import DialogueScriptReview from "./DialogueScriptReview";
import ReviewQuestion from "./ReviewQuestion";

/**
 * Render one normalized group (passage / image / audio + all questions) in
 * the review panel.
 *
 * Note: For fill-blank groups the shared `group.content` is suppressed —
 * the "passage" there is the question stem itself, which QuestionRenderer
 * already prints, so showing it twice was confusing.
 */
const ReviewGroup = ({ group, numberMap, highlightedQuestionId, theme }) => {
  const hasAudio = !!group.audioUrl;
  const hasImage = !!group.imageUrl;
  const isDialogue = group.type === "dialogue-group";
  const isFillBlank = group.type === "fill-blank";
  const hasContent = !!group.content && !isFillBlank;

  const items = isDialogue ? group.childQuestions || [] : group.questions || [];

  const hasLeftPane = isDialogue || hasContent || hasImage;

  return (
    <Box>
      {/* Header chips */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        {group.partNumber !== undefined && (
          <Chip
            label={`Part ${group.partNumber}`}
            size="small"
            variant="outlined"
          />
        )}
        {hasAudio && (
          <Chip
            icon={<VolumeUpIcon />}
            label="Listening"
            size="small"
            color="info"
            variant="outlined"
          />
        )}
        {isDialogue && (
          <Chip
            label="Dialogue"
            size="small"
            color="secondary"
            variant="outlined"
          />
        )}
      </Box>

      {/* Audio */}
      {hasAudio && (
        <Box sx={{ mb: 2 }}>
          <audio
            controls
            src={group.audioUrl}
            style={{ width: "100%", borderRadius: 4 }}
          />
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexDirection: { xs: "column", md: hasLeftPane ? "row" : "column" },
        }}
      >
        {/* Left: passage / image / dialogue script */}
        {hasLeftPane && (
          <Box
            sx={{
              width: { xs: "100%", md: isDialogue ? "55%" : "50%" },
              alignSelf: "flex-start",
              position: { xs: "static", md: "sticky" },
              top: { md: 0 },
            }}
          >
            {hasImage && (
              <Box sx={{ mb: 2, textAlign: "center" }}>
                <QuestionImage
                  imageUrl={group.imageUrl}
                  extraImageUrl={group.extraImageUrl}
                />
              </Box>
            )}
            {hasContent && (
              <Paper sx={{ p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
                <RichTextDisplay content={group.content} />
              </Paper>
            )}
            {isDialogue && (
              <DialogueScriptReview
                dialogueItem={group.dialogueQuestion}
                dialogueScript={group.dialogueScript}
                blanks={group.blanks}
              />
            )}
          </Box>
        )}

        {/* Right: questions */}
        <Box
          sx={{
            width: hasLeftPane
              ? { xs: "100%", md: isDialogue ? "45%" : "50%" }
              : "100%",
          }}
        >
          {items.length === 0 ? (
            <Typography color="text.secondary" fontStyle="italic">
              No comprehension questions in this group.
            </Typography>
          ) : (
            items.map((it, idx) => (
              <Box
                key={it.question?._id || idx}
                sx={{
                  mb: 2,
                  pb: 2,
                  borderBottom:
                    idx < items.length - 1
                      ? `1px dashed ${theme.palette.divider}`
                      : "none",
                  ...(highlightedQuestionId === it.question?._id && {
                    bgcolor: "rgba(2,136,209,0.04)",
                    borderRadius: 2,
                    p: 2,
                    pb: 2,
                  }),
                }}
              >
                <ReviewQuestion
                  item={it}
                  displayNumber={numberMap[it.question?._id]}
                  theme={theme}
                />
              </Box>
            ))
          )}
        </Box>
      </Box>

      {/* Dialogue parent explanation */}
      {isDialogue &&
        (group.dialogueExplanation ||
          group.dialogueQuestion?.question?.explanation) && (
          <Paper sx={{ mt: 3, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
            <Typography
              variant="subtitle2"
              color="primary"
              fontWeight={700}
              gutterBottom
            >
              Dialogue Explanation
            </Typography>
            <RichTextDisplay
              content={
                group.dialogueExplanation ||
                group.dialogueQuestion?.question?.explanation
              }
            />
          </Paper>
        )}
    </Box>
  );
};

export default ReviewGroup;
